import { AccountHeader } from '@/components/AccountHeader';
import { AppFooter } from '@/components/AppFooter';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Mail, Phone, Building, Calendar, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const Profile = () => {
  const { user, isAuthenticated, isInitializing } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth initialization to complete before checking authentication
    if (isInitializing) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isInitializing, navigate]);

  // Show loading state while initializing auth
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AccountHeader />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
        <AppFooter />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AccountHeader />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">My Profile</h1>
          <p className="text-muted-foreground mt-2">Manage your account information</p>
        </div>

        <div className="grid gap-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">First Name</label>
                  <p className="text-base font-semibold mt-1">{user.first_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Name</label>
                  <p className="text-base font-semibold mt-1">{user.last_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </label>
                  <p className="text-base font-semibold mt-1">{user.email || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone
                  </label>
                  <p className="text-base font-semibold mt-1">{user.phone ? `+91 ${user.phone}` : 'Not provided'}</p>
                </div>
                {user.alternative_number && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Alternative Phone</label>
                    <p className="text-base font-semibold mt-1">{user.alternative_number}</p>
                  </div>
                )}
                {user.date_of_birth && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date of Birth
                    </label>
                    <p className="text-base font-semibold mt-1">{user.date_of_birth}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Business Information */}
          {(user.company_name || user.business_email || user.business_number) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {user.company_name && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Company Name</label>
                      <p className="text-base font-semibold mt-1">{user.company_name}</p>
                    </div>
                  )}
                  {user.company_gst && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">GST Number</label>
                      <p className="text-base font-semibold mt-1">{user.company_gst}</p>
                    </div>
                  )}
                  {user.business_email && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Business Email</label>
                      <p className="text-base font-semibold mt-1">{user.business_email}</p>
                    </div>
                  )}
                  {user.business_number && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Business Phone</label>
                      <p className="text-base font-semibold mt-1">{user.business_number}</p>
                    </div>
                  )}
                  {user.whatsapp_number && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">WhatsApp Number</label>
                      <p className="text-base font-semibold mt-1">{user.whatsapp_number}</p>
                    </div>
                  )}
                  {user.company_url && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Company Website</label>
                      <p className="text-base font-semibold mt-1">
                        <a href={user.company_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {user.company_url}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Customer Code</label>
                  <p className="text-base font-semibold mt-1">{user.customer_code || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Customer Type</label>
                  <p className="text-base font-semibold mt-1">{user.customer_type || 'N/A'}</p>
                </div>
                {user.group && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Group</label>
                    <p className="text-base font-semibold mt-1">{user.group.name}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <p className="text-base font-semibold mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${user.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {user.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button onClick={() => navigate('/addresses')} variant="outline">
              Manage Addresses
            </Button>
            <Button onClick={() => navigate('/orders')} variant="outline">
              View Orders
            </Button>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
};

export default Profile;

